import { Box, Icon, SxProps, Theme, visuallyHidden } from "@outschool/backpack";
import { faBars, faSearch, faTimes } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  forSellerOrgPath,
  leadActivitiesPath,
  pathFromLocation,
  searchPath,
  sellerOrgPath,
  teachPath,
  teachTipsUrl
} from "@outschool/routes";
import { AuthTrigger, useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { CategoryNavbar, NavHelpLink } from "@outschool/ui-components-website";
import {
  ConditionalTrackingPageSection,
  Container,
  IconButton,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { Screen, useNodeRef } from "@outschool/ui-utils";
import React from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";

import * as Routes from "../../../shared/Routes";
import { usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { UserMode, useUserMode, useUserModeRootPath } from "../../lib/UserMode";
import Link from "../Link";
import DesktopHeaderNav from "./DesktopHeaderNav";
import GlobalSearchBar from "./GlobalSearchBar";
import HeaderLogo from "./HeaderLogo";
import HeaderMenu from "./HeaderMenu";
import HeaderNavItem from "./HeaderNavItem";

interface Props {
  trackingUniqueId?: string;
  isTransparent?: boolean;
  isWhiteText?: boolean;
  allowGlobalNavbar?: boolean;
}

export default function Header({
  trackingUniqueId,
  isTransparent,
  isWhiteText = true,
  allowGlobalNavbar = false
}: Props) {
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;
  const includeGlobalNavbar = !isEducatorMode && allowGlobalNavbar;
  const { t } = useTranslation("client\\components\\nav\\Header");

  return (
    <ConditionalTrackingPageSection
      name="header"
      uniqueId={trackingUniqueId ? trackingUniqueId : "header"}
    >
      {setImpressionNode => (
        <Box ref={setImpressionNode}>
          {isTransparent ? (
            <TransparentHeader
              isWhiteText={isWhiteText}
              includeGlobalNavbar={includeGlobalNavbar}
              t={t}
            />
          ) : (
            <StandardHeader
              hideFindClasses={isEducatorMode}
              includeGlobalNavbar={includeGlobalNavbar}
            />
          )}
        </Box>
      )}
    </ConditionalTrackingPageSection>
  );
}

type HeaderFlexContainer = React.PropsWithChildren<{
  sx?: SxProps;
  extended?: boolean;
}>;

function HeaderFlexContainer({
  sx = {},
  extended = false,
  ...props
}: HeaderFlexContainer) {
  return (
    <Container size={extended ? "large" : "medium"}>
      <Box
        flex
        sx={[
          {
            alignItems: "center",
            justifyContent: "space-between"
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
        {...props}
      />
    </Container>
  );
}

function StandardHeaderLeft() {
  const rootPath = useUserModeRootPath();
  return (
    <Link to={rootPath} trackingName="nav_logo" reloadDocument>
      <HeaderNavItem sx={{ paddingX: 0 }}>
        <HeaderLogo isWhiteText={false} />
      </HeaderNavItem>
    </Link>
  );
}

function ListingsPageLink({ t }: { t: TFunction }) {
  return (
    <Link to={searchPath()} trackingName="nav_find_classes">
      <HeaderNavItem>
        <Icon
          icon={faSearch}
          sx={{
            fontSize: "1.333em"
          }}
        />
      </HeaderNavItem>
      <span style={visuallyHidden}>{t`Search`}</span>
    </Link>
  );
}

function MobileHeaderMenuButton({
  isTransparent,
  isWhiteText,
  t
}: {
  isTransparent?: boolean;
  isWhiteText?: boolean;
  t: TFunction;
}) {
  const [numCloseCount, setNumCloseCount] = React.useState(0);
  const [mobileHeaderMenuOpen, setMobileHeaderMenuOpen] = React.useState(false);
  const [mobileHeaderMenuOpenNode, setMobileHeaderMenuOpenNode] =
    useNodeRef<HTMLDivElement>();
  const { trackTouch: trackMobileHeaderMenuOpenButtonTouch } =
    useImpressionTracking({
      node: mobileHeaderMenuOpenNode,
      trackingLabel: "mobile_header_menu_open_button",
      uniqueId: `mobile_header_menu_open_button-${numCloseCount}`,
      sharedProperties: {
        numCloseCount
      }
    });
  const closeHeaderMenu = () => {
    setMobileHeaderMenuOpen(false);
    setNumCloseCount(numCloseCount + 1);
  };
  return (
    <>
      <HeaderNavItem
        variant={isTransparent ? "transparent" : undefined}
        onClick={() => {
          trackMobileHeaderMenuOpenButtonTouch();
          setMobileHeaderMenuOpen(true);
        }}
        ref={setMobileHeaderMenuOpenNode}
        role="menuitem"
      >
        <Icon
          icon={faBars}
          sx={{
            fontSize: "1.333em",
            color: isWhiteText ? "common.white" : "gray"
          }}
        />
        <span style={visuallyHidden}>{t`Menu`}</span>
      </HeaderNavItem>
      <MobileHeaderMenu open={mobileHeaderMenuOpen} onClose={closeHeaderMenu} />
    </>
  );
}

function StandardHeaderRight({ hideFindClasses, t }) {
  const isMobile = Screen.useIsMobile();
  return isMobile ? (
    <Box
      flex
      sx={{
        gap: "1.5em",
        paddingY: "0.5em"
      }}
    >
      {!hideFindClasses && <ListingsPageLink t={t} />}
      <MobileHeaderMenuButton t={t} />
    </Box>
  ) : (
    <DesktopHeaderNav />
  );
}

interface StandardHeaderProps {
  sx?: any;
  hideFindClasses?: boolean;
  leftChildren?: any;
  rightChildren?: any;
  includeGlobalNavbar?: boolean;
}
export const StandardHeader = ({
  sx,
  hideFindClasses,
  leftChildren,
  rightChildren,
  includeGlobalNavbar
}: StandardHeaderProps) => {
  const { t } = useTranslation("client\\components\\nav\\Header"); //StickyMobileHeader imports this as well
  const { currentUser } = useSession();

  const isOrg = currentUser && currentUser.sellerOrg;
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;
  const isOfferAdmin = usePricingOfferAdmin();

  if (leftChildren === undefined) {
    // if null, omit left header
    leftChildren = <StandardHeaderLeft />;
  }
  if (rightChildren === undefined) {
    // if null, omit right header
    rightChildren = (
      <StandardHeaderRight hideFindClasses={hideFindClasses} t={t} />
    );
  }
  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "common.white",
        boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)",
        ...sx
      }}
    >
      <HeaderFlexContainer
        extended={Boolean(isOrg || (isOfferAdmin && isEducatorMode))}
        sx={{
          gap: "1.5em"
        }}
      >
        {leftChildren}
        {!isEducatorMode && <GlobalSearchBar t={t} />}
        {rightChildren}
      </HeaderFlexContainer>
      {includeGlobalNavbar && (
        <CategoryNavbar
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("md")]: {
              borderTop: "1px solid"
            },
            borderColor: "#808080"
          })}
        />
      )}
    </Box>
  );
};

function TransparentHeader({ isWhiteText, includeGlobalNavbar, t }) {
  const isMobile = Screen.useIsMobile();
  const rootPath = useUserModeRootPath();
  return (
    <Box
      sx={{
        backgroundColor: "common.white"
      }}
    >
      <HeaderFlexContainer>
        <Box flex>
          <Link to={rootPath} trackingName="nav_logo" reloadDcument>
            <HeaderNavItem
              variant={isWhiteText ? "transparent" : "standard"}
              sx={
                !isWhiteText && {
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.5)"
                  }
                }
              }
            >
              <HeaderLogo isWhiteText={isWhiteText} />
            </HeaderNavItem>
          </Link>
        </Box>

        {isMobile ? (
          <MobileHeaderMenuButton
            isTransparent={true}
            isWhiteText={isWhiteText}
            t={t}
          />
        ) : (
          <DesktopTransparentHeaderNav isWhiteText={isWhiteText} t={t} />
        )}
      </HeaderFlexContainer>

      {includeGlobalNavbar && (
        <CategoryNavbar
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("md")]: {
              borderTop: "1px solid"
            },
            borderBottom: "1px solid",
            borderColor: "#808080"
          })}
        />
      )}
    </Box>
  );
}

function DesktopTransparentHeaderNav({ isWhiteText, t }) {
  const { isLoggedIn } = useSession();
  const { pathname } = useLocation();

  const isTeacherPath = pathname === teachPath();
  const isSellerOrgPath =
    pathname === forSellerOrgPath() || pathname === sellerOrgPath();

  return (
    <Box
      flex
      sx={{
        gap: "0.5em"
      }}
    >
      {!isLoggedIn && isTeacherPath && (
        <ExternalLink url={teachTipsUrl()} trackingName="nav_library_link">
          <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
            {t`Educator Library`}
          </HeaderNavItem>
        </ExternalLink>
      )}

      <Link to={leadActivitiesPath()} trackingName="nav_teach_link">
        <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
          {t`Teach`}
        </HeaderNavItem>
      </Link>

      <NavHelpLink>
        <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
          {t`Help`}
        </HeaderNavItem>
      </NavHelpLink>
      {!isLoggedIn && (
        <Link to={Routes.loginPath()} trackingName="nav_login_link">
          <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
            {t`Log In`}
          </HeaderNavItem>
        </Link>
      )}
      {!isLoggedIn && !isSellerOrgPath && (
        <Link
          to={addParamsToUrl(pathFromLocation(window.location), {
            authTrigger: AuthTrigger.JOIN_OUTSCHOOL,
            signup: true
          })}
          trackingName="nav_signup_link"
          useHardNav={true}
        >
          <HeaderNavItem
            variant={isWhiteText ? "transparent" : "standard"}
            sx={{ color: isWhiteText ? "white" : "#4B01D4" }}
          >
            {t`Join`}
          </HeaderNavItem>
        </Link>
      )}
    </Box>
  );
}

interface MobileHeaderMenuProps {
  open: boolean;
  onClose: () => void;
}
export function MobileHeaderMenu({
  open,
  onClose
}: MobileHeaderMenuProps): React.ReactPortal {
  return ReactDOM.createPortal(
    <Box>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 2000,
          pointerEvents: open ? "initial" : "none",
          opacity: open ? 1 : 0,
          transition: "opacity 0.3s ease"
        }}
        onClick={onClose}
      />
      <Box
        flex
        sx={{
          flexDirection: "column",
          position: "fixed",
          right: open ? 0 : "-100vw",
          top: 0,
          bottom: 0,
          backgroundColor: "common.white",
          zIndex: 2001,
          transition: "right 0.3s ease",
          minWidth: "75vw"
        }}
      >
        <Box
          flex
          sx={{
            padding: "0.5em",
            justifyContent: "flex-end",
            borderBottom: "1px solid",
            borderColor: "#ecf1f8"
          }}
        >
          <IconButton
            aria-label="Close"
            icon={faTimes}
            iconProps={{ sx: { fontSize: "1.333em" } }}
            onClick={onClose}
          />
        </Box>
        <HeaderMenu onClose={onClose} />
      </Box>
    </Box>,
    document.body
  );
}
